import { Tooltip } from '@mantine/core'
import { useContext } from 'react'

import { AutomatedRuleConditionKey } from 'src/graphql/types/automatedRules'
import IconAlert from 'src/icons/IconAlert'
import IconFlag from 'src/icons/IconFlag'
import IconJobs from 'src/icons/IconJobs'
import IconLink from 'src/icons/IconLink'
import IconList from 'src/icons/IconList'
import IconMissedCall from 'src/icons/IconMissedCall'
import IconPhoneCall from 'src/icons/IconPhoneCall'
import IconSparkle from 'src/icons/IconSparkle'
import { formatSnakeValue, formatStepType } from 'src/lib/formatters'
import { formatTerminatedReason } from 'src/lib/terminatedReason.utils'
import { RecruitersContext } from 'src/context/RecruitersContext'

const ICON_CLASS = 'h-3 w-3 fill-none stroke-current'
const ICON_CLASS_FILLED = 'h-3 w-3 fill-current'

type IconRenderer = () => JSX.Element

const KEY_ICON: Record<string, IconRenderer> = {
  [AutomatedRuleConditionKey.tagId]: () => <IconFlag className={ICON_CLASS} />,
  [AutomatedRuleConditionKey.jobListingType]: () => (
    <IconJobs className={ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.jobListingId]: () => (
    <IconList className={ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.linkType]: () => (
    <IconLink className={ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.callCount]: () => (
    <IconPhoneCall className={ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.missedOutboundCallCount]: () => (
    <IconMissedCall className={ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.notInterestedReason]: () => (
    <IconAlert className={ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.disqualifiedReason]: () => (
    <IconAlert className={ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.terminatedReason]: () => (
    <IconAlert className={ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.eligibleForRehire]: () => (
    <IconAlert className={ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.isPermanentlyDisqualified]: () => (
    <IconAlert className={ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.recruiterId]: () => (
    <IconList className={ICON_CLASS} />
  ),
  step: () => <IconSparkle className={ICON_CLASS_FILLED} />,
}

type ConditionLike = {
  key: string
  value?: string | null
  stepType?: string | null
  stepStatus?: string | null
  customStep?: { label?: string | null } | null
}

class EmployerFormatter {
  static split(value: string | null | undefined): string[] {
    return (value ?? '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
  }

  static idToNameMap(
    recruiters: Array<{ value: string; label: string }>
  ): Record<string, string> {
    const map: Record<string, string> = {}
    for (const r of recruiters ?? []) {
      map[r.value] = r.label
    }
    return map
  }

  static display(
    value: string | null | undefined,
    recruiters: Array<{ value: string; label: string }>
  ): string {
    const ids = EmployerFormatter.split(value)
    const byId = EmployerFormatter.idToNameMap(recruiters)
    const names = ids.map((id) => byId[id] ?? id)
    return names.join(', ')
  }

  static tooltipLines(
    value: string | null | undefined,
    recruiters: Array<{ value: string; label: string }>
  ): string[] {
    const ids = EmployerFormatter.split(value)
    const byId = EmployerFormatter.idToNameMap(recruiters)
    return ids.map((id) => byId[id] ?? id)
  }
}

const splitValues = (value: string | null | undefined): string[] =>
  (value ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)

const formatValue = (raw: string, key?: string): string => {
  if (!raw) return ''
  const trimmed = raw.trim()
  // Eligible-for-rehire stores the boolean as 'true'/'false'; show Yes/No.
  if (key === AutomatedRuleConditionKey.eligibleForRehire) {
    return trimmed === 'true' ? 'Rehire Eligible: Yes' : 'Rehire Eligible: No'
  }
  // Permanently-disqualified stores the boolean as 'true'/'false'; show Yes/No.
  if (key === AutomatedRuleConditionKey.isPermanentlyDisqualified) {
    return trimmed === 'true'
      ? 'Permanently Disqualified: Yes'
      : 'Permanently Disqualified: No'
  }
  if (!/^[A-Z_]+$/.test(trimmed)) return trimmed
  // Terminated reasons carry label overrides (e.g. "Company Policy"); other
  // condition values use the generic SNAKE_CASE → Title Case formatter.
  return key === AutomatedRuleConditionKey.terminatedReason
    ? formatTerminatedReason(trimmed)
    : formatSnakeValue(trimmed)
}

const formatChipDisplay = (condition: ConditionLike): string => {
  if (condition.key === 'step') {
    const type =
      condition.customStep?.label ??
      (condition.stepType ? formatStepType(condition.stepType) : '')
    const status = condition.stepStatus
      ? formatSnakeValue(condition.stepStatus)
      : ''
    return [type, status].filter(Boolean).join(' · ')
  }
  return splitValues(condition.value)
    .map((v) => formatValue(v, condition.key))
    .join(', ')
}

const formatTooltipLines = (condition: ConditionLike): string[] => {
  if (condition.key === 'step') {
    return [formatChipDisplay(condition)]
  }
  const values = splitValues(condition.value)
  if (values.length <= 1) return [formatChipDisplay(condition)]
  return values.map((v) => formatValue(v, condition.key))
}

type Variant = 'event' | 'default'

const variantClasses = (variant: Variant): string =>
  variant === 'event'
    ? 'border-doubleNickel-brand-100 bg-doubleNickel-brand-25 text-doubleNickel-brand-700 hover:border-doubleNickel-brand-300'
    : 'border-doubleNickel-gray-200 bg-white text-doubleNickel-gray-700 hover:border-doubleNickel-gray-400'

const ConditionChip = ({ condition }: { condition: ConditionLike }) => {
  const recruiters =
    (useContext(RecruitersContext) as { value: string; label: string }[]) ?? []
  const renderIcon = KEY_ICON[condition.key]
  const variant: Variant = condition.key === 'step' ? 'event' : 'default'
  const display =
    condition.key === AutomatedRuleConditionKey.recruiterId
      ? EmployerFormatter.display(condition.value, recruiters)
      : formatChipDisplay(condition)
  const tooltipLines =
    condition.key === AutomatedRuleConditionKey.recruiterId
      ? EmployerFormatter.tooltipLines(condition.value, recruiters)
      : formatTooltipLines(condition)

  const tooltipLabel =
    tooltipLines.length > 1 ? (
      <ul className="m-0 list-none p-0">
        {tooltipLines.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    ) : (
      tooltipLines[0]
    )

  return (
    <Tooltip
      label={tooltipLabel}
      withArrow
      withinPortal
      position="top"
      multiline
      maw={320}
    >
      <span
        className={`inline-flex max-w-[180px] cursor-default items-center gap-1 truncate rounded-[5px] border px-1.5 py-px text-[10px] font-medium leading-none transition-colors ${variantClasses(
          variant
        )}`}
      >
        {renderIcon ? renderIcon() : null}
        <span className="truncate">{display}</span>
      </span>
    </Tooltip>
  )
}

export default ConditionChip
